import React, { useState, useRef, useEffect } from "react";

import { Textarea } from "app/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "app/components/ui/collapsible";
import { formatUSDate } from "utils/date";

export type Note = {
  id: string;
  author: string;
  date: string;
  content: string;
};

export type AdminNotesProps = {
  isFormOpen: boolean;
  isNotesOpen: boolean;
  setIsNotesOpen: (isNotesOpen: boolean) => void;
  notes: Note[];
  isLoading: boolean;
  isError?: boolean;
  onAddNote: (content: string) => void;
  canAdd?: boolean;
  isAddingNote?: boolean;
};

export function AdminNotes({
  isFormOpen,
  isNotesOpen,
  setIsNotesOpen,
  notes,
  isLoading,
  isError,
  onAddNote,
  canAdd = true,
  isAddingNote = false,
}: AdminNotesProps) {
  const [note, setNote] = useState("");
  const [textareaHeight, setTextareaHeight] = useState(48); // Initial height in pixels
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      
      // Calculate line height (approximately 20px per line)
      const lineHeight = 20;
      const maxLines = 7;
      const maxHeight = lineHeight * maxLines;
      
      // Set height based on content, but cap at maxHeight
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
      setTextareaHeight(newHeight);
      
      // Always add custom scrollbar class and set overflow
      textarea.classList.add('custom-scrollbar');
      if (textarea.scrollHeight > maxHeight) {
        textarea.style.overflowY = 'auto';
        textarea.style.overflowX = 'hidden';
      } else {
        textarea.style.overflowY = 'hidden';
        textarea.style.overflowX = 'hidden';
      }
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [note]);

  const handleAdd = () => {
    if (note.trim() && !isAddingNote) {
      onAddNote(note);
      setNote("");
      setTextareaHeight(48); // Reset height to initial
      if (textareaRef.current) {
        textareaRef.current.style.height = '48px'; // Reset height to initial
        textareaRef.current.style.overflowY = 'hidden'; // Reset overflow
        textareaRef.current.style.overflowX = 'hidden'; // Reset overflow
      }
    }
  };

  // Calculate the available height for the notes container
  const getNotesContainerHeight = () => {
    const baseHeight = isFormOpen && isNotesOpen
      ? 'calc(100vh - 36.575rem)'
      : isNotesOpen
      ? 'calc(100vh - 16.85rem)'
      : '0';
    
    const maxHeight = isFormOpen && isNotesOpen
      ? '17.5rem'
      : isNotesOpen
      ? '41.75rem'
      : '0';
    
    // Subtract the textarea height (plus padding and margins) from the available space
    const textareaTotalHeight = textareaHeight + 24; // 24px for padding and margins
    
    // Convert maxHeight to pixels for calculation
    const maxHeightInPx = isFormOpen && isNotesOpen
      ? 280 // 17.5rem = 280px
      : isNotesOpen
      ? 738 // 46.125rem = 738px
      : 0;
    
    // Calculate the adjusted max height by subtracting textarea height
    const adjustedMaxHeight = Math.max(0, maxHeightInPx - textareaTotalHeight);
    
    const adjustedHeight = `calc(${baseHeight} - ${textareaTotalHeight}px)`;
    
    return {
      height: adjustedHeight,
      maxHeight: `${adjustedMaxHeight}px`
    };
  };

  const notesContainerStyle = getNotesContainerHeight();
  
  return (
    <Collapsible
      open={isNotesOpen}
      onOpenChange={setIsNotesOpen}
      className="overflow-hidden bg-white rounded-lg"
    >
      <div className="flex items-center justify-between bg-[#DDE1E6] p-4 rounded-t-lg">
        <div className="flex gap-4 items-center">
          <h2 className="font-serif text-[#21272A] text-base font-semibold leading-[1.2rem]">
            Admin Notes
          </h2>
          <span className="inline-flex justify-center items-center p-1 border-[0.6px] border-[#A2A9B0] rounded text-[#5B73F1] text-[0.625rem] font-extrabold leading-3 tracking-[-0.00625rem]">
            {notes?.length?.toString().padStart(2, "0") || '00'}
          </span>
        </div>
        <CollapsibleTrigger asChild>
          <button
            className="inline-flex items-center justify-center bg-white hover:bg-[#FAFAFA] size-5 rounded"
            tabIndex={0}
            aria-label={isNotesOpen ? "Collapse" : "Expand"}
          >
            <img 
              src="/images/chevron.svg" 
              alt="" 
              className={`transition-transform duration-200 ${isNotesOpen ? '':'rotate-180'}`}
            />
          </button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent
        className={`overflow-y-auto transition-all duration-100 ease-in-out custom-scrollbar`}
        style={{ height: notesContainerStyle.height }}
      >
        {isLoading && notes?.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm font-normal leading-[1.225rem]">
            <span className="text-[#697077]">Loading notes...</span>
          </div>
        ) : isError ? (
          <div className="flex justify-center items-center h-full text-sm font-normal leading-[1.225rem]">
            <span className="text-red-500">Failed to load notes</span>
          </div>
        ) : !canAdd ? (
          <div className="flex items-center justify-center h-full text-sm font-normal leading-[1.225rem]">
            <span className="text-[#697077]">No notes found.</span>
          </div>
        ) : notes?.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm font-normal leading-[1.225rem]">
            <span className="text-[#697077]">No notes yet.</span>
          </div>
        ) : (
          <div className="overflow-y-auto h-full custom-scrollbar">
            {notes?.map((n: any, index: number) => (
              <div
                key={n.id}
                className={`space-y-1 px-4 py-3 ${index === notes.length - 1 ? '' : 'border-b border-[#DDE1E6]'}`}
              >
                <div className="flex justify-between items-center">
                  <h6 className="text-[#697077] text-xs font-light leading-normal">
                    {n.author?.firstName} {n.author?.lastName}
                  </h6>
                  <span className="inline-block text-[#697077] text-xs font-light leading-normal">
                    {formatUSDate(n.createdAt)}
                  </span>
                </div>
                <p className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                  {n.text}
                </p>
              </div>
            ))}
          </div>
        )}
      </CollapsibleContent>
      {canAdd && (
        <div className={`relative w-full ${isNotesOpen ? '':'mt-3'}`}>
          <div className="relative w-full">
            <Textarea
              ref={textareaRef}
              className="bg-white w-full min-h-12 pl-4 pr-12 py-3 border-[1.2px] border-[#5B73F1] rounded-[0.625rem] text-black/80 placeholder:text-[#697077] text-xs font-normal leading-normal focus-visible:ring-0 focus-visible:border focus:border-blue-400 resize-none"
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                if (!isNotesOpen) {
                  setIsNotesOpen(true);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder="Enter notes here..."
              aria-label="Enter notes here"
              rows={1}
              tabIndex={0}
              style={{
                overflowX: 'hidden',
                overflowY: 'auto',
                paddingRight: 'calc(1rem + 24px)' // Extra padding for scrollbar
              }}
            />
            <div className="absolute right-4 top-6 -translate-y-1/2">
              <button
                type="button"
                className="inline-flex items-center justify-center size-6 bg-[#C1C7CD] hover:bg-[#C1C7CD80] rounded-full cursor-pointer"
                aria-label="Add note"
                onClick={handleAdd}
                disabled={isAddingNote || !note.trim()}
                tabIndex={0}
              >
                <img src="/images/arrow.svg" alt="Arrow" />
              </button>
            </div>
          </div>
        </div>
      )}
    </Collapsible>
  );
}
